import { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format, isValid } from "date-fns";
import { cn, formatCurrency } from "@/lib/utils";

function safeFormatDate(date: string | null | undefined, fmt: string): string {
  if (!date) return "N/A";
  const d = new Date(date);
  return isValid(d) ? format(d, fmt) : "N/A";
}
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/loans/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DollarSign, Wallet, Banknote, Building2, Landmark, CreditCard,
  Calendar, Clock, CheckCircle2, AlertTriangle, Info, ArrowRight,
  Loader, PiggyBank, TrendingUp, Percent, User, FileText,
  AlertCircle, ChevronRight, Receipt, ArrowLeft,
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────
export interface PaymentFormData {
  installmentNumber: number;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  notes: string;
  receiptUrl?: string;
}

interface ScheduleItem {
  id: number;
  installmentNumber: number;
  principalAmount: number;
  interestAmount: number;
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;
  dueDate: string;
  status: string;
  daysOverdue: number;
}

interface LoanData {
  id: number;
  loanCode: string;
  amount: number;
  interestRate: number;
  termMonths: number;
  monthlyPayment: number;
  dueAmount: number;
  totalPaid: number;
  status: string;
  purpose: string;
  loanType: string;
  applicationDate: string;
  approvalDate?: string;
  disbursementDate?: string;
  maturityDate?: string;
  memberCode?: string;
  memberName?: string;
  memberPhone?: string;
  schedule?: ScheduleItem[];
}

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loan: LoanData;
  mode: "admin" | "member";
  onSubmit: (data: PaymentFormData) => void;
  isLoading: boolean;
}

// ─── Payment Method Config ─────────────────────────────────────────────────
const PAYMENT_METHODS = [
  { value: "Cash", icon: Wallet, label: "Cash", desc: "Physical cash payment" },
  { value: "Bank Transfer", icon: Landmark, label: "Bank Transfer", desc: "Wire or EFT transfer" },
  { value: "Mobile Money", icon: SmartphoneIcon, label: "Mobile Money", desc: "Momo / Airtel Money" },
  { value: "Card", icon: CreditCard, label: "Card", desc: "Debit or credit card" },
  { value: "Salary Deduction", icon: Building2, label: "Payroll Deduction", desc: "Deducted from salary" },
];

function SmartphoneIcon(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
      <line x1="12" x2="12.01" y1="18" y2="18" />
    </svg>
  );
}

// ─── Loan Summary Card ─────────────────────────────────────────────────────
function LoanSummaryCard({ loan, progress }: { loan: LoanData; progress: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl border border-green-100 bg-gradient-to-br from-green-50/80 via-white to-emerald-50/40 backdrop-blur-sm overflow-hidden shadow-sm"
    >
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
              <Receipt className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">Loan Summary</p>
              <p className="text-sm font-bold text-gray-900">{loan.loanCode}</p>
            </div>
          </div>
          <StatusBadge status={loan.status} />
        </div>

        <div className="space-y-2.5">
          <InfoRow icon={User} label="Member" value={loan.memberName || "N/A"} />
          <InfoRow icon={FileText} label="Type" value={loan.loanType || "Monthly"} />
          <InfoRow icon={Percent} label="Interest" value={`${loan.interestRate}%`} />
          <InfoRow icon={TrendingUp} label="Installment" value={formatCurrency(loan.monthlyPayment)} />
        </div>

        <div className="border-t border-green-100 pt-3 space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-500">Loan Amount</span>
            <span className="text-sm font-bold text-gray-900">{formatCurrency(loan.amount)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-500">Total Paid</span>
            <span className="text-sm font-semibold text-green-600">{formatCurrency(loan.totalPaid || 0)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-500">Outstanding</span>
            <span className="text-sm font-bold text-orange-600">{formatCurrency(loan.dueAmount || 0)}</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Payment Progress</span>
            <span className="font-semibold text-gray-600">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-green-100 [&>div]:bg-gradient-to-r [&>div]:from-green-500 [&>div]:to-emerald-500" />
        </div>
      </div>
    </motion.div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
      <span className="text-xs text-gray-400 flex-1">{label}</span>
      <span className="text-xs font-medium text-gray-700">{value}</span>
    </div>
  );
}

// ─── Upcoming Payment Card ─────────────────────────────────────────────────
function UpcomingPaymentCard({ nextDue }: { nextDue: ScheduleItem | null }) {
  if (!nextDue) return null;

  const dueDate = new Date(nextDue.dueDate);
  const now = new Date();
  const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = daysUntilDue < 0;
  const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 3;
  const amountDue = nextDue.totalAmount - (nextDue.paidAmount || 0);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className={cn(
        "rounded-2xl border overflow-hidden shadow-sm backdrop-blur-sm",
        isOverdue ? "border-red-200 bg-gradient-to-br from-red-50/80 to-white" :
        isDueSoon ? "border-amber-200 bg-gradient-to-br from-amber-50/80 to-white" :
        "border-blue-100 bg-gradient-to-br from-blue-50/80 to-white"
      )}
    >
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className={cn(
            "h-7 w-7 rounded-xl flex items-center justify-center",
            isOverdue ? "bg-red-100" : isDueSoon ? "bg-amber-100" : "bg-blue-100"
          )}>
            <Calendar className={cn(
              "h-3.5 w-3.5",
              isOverdue ? "text-red-600" : isDueSoon ? "text-amber-600" : "text-blue-600"
            )} />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Upcoming Payment</span>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-400">Due Date</span>
            <span className="text-xs font-semibold text-gray-700">{format(dueDate, "MMM dd, yyyy")}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-400">Amount Due</span>
            <span className={cn(
              "text-sm font-bold",
              isOverdue ? "text-red-600" : "text-gray-900"
            )}>{formatCurrency(amountDue)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-400">Installment</span>
            <span className="text-xs font-semibold text-gray-600">#{nextDue.installmentNumber}</span>
          </div>
        </div>

        <div className={cn(
          "rounded-xl px-3 py-2 flex items-center gap-2 text-xs font-medium",
          isOverdue ? "bg-red-100 text-red-700" : isDueSoon ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
        )}>
          {isOverdue ? (
            <><AlertCircle className="h-3.5 w-3.5" /> {Math.abs(daysUntilDue)} days overdue</>
          ) : isDueSoon ? (
            <><Clock className="h-3.5 w-3.5" /> Due in {daysUntilDue} {daysUntilDue === 1 ? "day" : "days"}</>
          ) : (
            <><Clock className="h-3.5 w-3.5" /> {daysUntilDue} days remaining</>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Installment Select ────────────────────────────────────────────────────
function InstallmentSelect({
  schedule,
  value,
  onChange,
  disabled,
}: {
  schedule: ScheduleItem[];
  value: number;
  onChange: (num: number) => void;
  disabled?: boolean;
}) {
  const unpaid = schedule.filter(s => s.status !== "paid");

  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-gray-700">Step 1 &middot; Installment</Label>
      <p className="text-xs text-gray-400">Select the installment to pay</p>
      <Select
        value={String(value || "")}
        onValueChange={(val) => onChange(parseInt(val))}
        disabled={disabled}
      >
        <SelectTrigger className="h-12 rounded-xl border-gray-200 bg-white shadow-sm hover:border-green-300 transition-colors data-[state=open]:border-green-400 data-[state=open]:ring-2 data-[state=open]:ring-green-100">
          <SelectValue placeholder="Choose an installment" />
        </SelectTrigger>
        <SelectContent className="rounded-xl">
          {unpaid.map((s) => {
            const remaining = s.totalAmount - (s.paidAmount || 0);
            const isOverdue = s.status === "overdue";
            const isPartial = s.status === "partial";
            return (
              <SelectItem key={s.id} value={String(s.installmentNumber)} className="py-3">
                <div className="flex items-center justify-between w-full gap-4">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold",
                      isOverdue ? "bg-red-100 text-red-600" : isPartial ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-600"
                    )}>
                      #{s.installmentNumber}
                    </div>
                    <div>
                      <p className="text-sm font-medium">Installment #{s.installmentNumber}</p>
                      <p className="text-xs text-gray-400">{safeFormatDate(s.dueDate, "MMM dd, yyyy")}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{formatCurrency(remaining)}</p>
                    <Badge className={cn(
                      "text-[10px] px-1.5 py-0",
                      s.status === "overdue" && "bg-red-100 text-red-700",
                      s.status === "pending" && "bg-gray-100 text-gray-600",
                      s.status === "partial" && "bg-amber-100 text-amber-700",
                    )}>
                      {s.status === "overdue" ? "Overdue" : s.status === "partial" ? "Partial" : "Pending"}
                    </Badge>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Payment Method Selector ───────────────────────────────────────────────
function PaymentMethodSelector({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-semibold text-gray-700">Step 3 &middot; Payment Method</Label>
        <p className="text-xs text-gray-400 mt-0.5">Choose how you want to pay</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {PAYMENT_METHODS.map((method) => {
          const selected = value === method.value;
          return (
            <motion.button
              key={method.value}
              type="button"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChange(method.value)}
              disabled={disabled}
              className={cn(
                "relative flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all",
                selected
                  ? "border-green-500 bg-green-50/80 shadow-sm shadow-green-100"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm",
                disabled && "opacity-50 cursor-not-allowed"
              )}
              aria-pressed={selected}
              aria-label={method.label}
            >
              {selected && (
                <motion.div
                  layoutId="paymentCheck"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-2 right-2 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center"
                >
                  <CheckCircle2 className="h-3 w-3 text-white" />
                </motion.div>
              )}
              <div className={cn(
                "h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors",
                selected ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"
              )}>
                <method.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-sm font-semibold transition-colors",
                  selected ? "text-green-700" : "text-gray-800"
                )}>{method.label}</p>
                <p className="text-[11px] text-gray-400 truncate">{method.desc}</p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Payment Summary Bar ───────────────────────────────────────────────────
function PaymentSummaryBar({
  amount,
  remainingBalance,
  installmentNumber,
  paymentMethod,
  paymentDate,
}: {
  amount: number;
  remainingBalance: number;
  installmentNumber: number;
  paymentMethod: string;
  paymentDate: string;
}) {
  const newRemaining = Math.max(0, remainingBalance - amount);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50/60 p-4 space-y-3 shadow-sm"
    >
      <div className="flex items-center gap-2">
        <div className="h-6 w-6 rounded-lg bg-green-100 flex items-center justify-center">
          <Info className="h-3.5 w-3.5 text-green-600" />
        </div>
        <span className="text-xs font-semibold text-green-700 uppercase tracking-wider">Payment Summary</span>
      </div>
      <p className="text-xs text-green-600/80">You are about to record</p>
      <div className="space-y-2">
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-gray-500">Amount</span>
          <span className="text-lg font-bold text-green-700">{formatCurrency(amount)}</span>
        </div>
        <div className="h-px bg-green-200/60" />
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-gray-500">Remaining After Payment</span>
          <span className="text-sm font-semibold text-gray-700">{formatCurrency(newRemaining)}</span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-gray-500">Installment</span>
          <span className="text-xs font-semibold text-gray-600">#{installmentNumber}</span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-gray-500">Method</span>
          <span className="text-xs font-medium text-gray-600">{paymentMethod}</span>
        </div>
        {paymentDate && (
          <div className="flex justify-between items-baseline">
            <span className="text-xs text-gray-500">Date</span>
            <span className="text-xs font-medium text-gray-600">{format(new Date(paymentDate), "MMM dd, yyyy")}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Help Card ─────────────────────────────────────────────────────────────
function HelpCard() {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="h-7 w-7 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
          <Info className="h-3.5 w-3.5 text-gray-500" />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-700">Need Help?</p>
          <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">
            Payments are verified by leaders before updating your balance. 
            Keep your transaction receipt for reference.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Success Animation ─────────────────────────────────────────────────────
function SuccessOverlay({ show }: { show: boolean }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-2xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="text-center"
          >
            <motion.div
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3"
            >
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </motion.div>
            <p className="text-lg font-bold text-gray-900">Payment Recorded!</p>
            <p className="text-sm text-gray-500 mt-1">The payment has been submitted successfully.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Main Payment Modal ────────────────────────────────────────────────────
export default function PaymentModal({ open, onOpenChange, loan, mode, onSubmit, isLoading }: PaymentModalProps) {
  const [installmentNumber, setInstallmentNumber] = useState(0);
  const [amount, setAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDate, setPaymentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [notes, setNotes] = useState("");
  const [receiptUrl, setReceiptUrl] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const progress = useMemo(() => {
    if (!loan.amount) return 0;
    return Math.min(100, Math.round(((loan.totalPaid || 0) / loan.amount) * 100));
  }, [loan]);

  const nextDue = useMemo(() => {
    if (!loan.schedule) return null;
    return loan.schedule.find(s => s.status === "pending" || s.status === "overdue");
  }, [loan]);

  const unpaidSchedule = useMemo(() => {
    return (loan.schedule || []).filter(s => s.status !== "paid");
  }, [loan.schedule]);

  const selectedSchedule = useMemo(() => {
    if (!installmentNumber || !loan.schedule) return null;
    return loan.schedule.find(s => s.installmentNumber === installmentNumber) || null;
  }, [installmentNumber, loan.schedule]);

  const remainingForSelected = useMemo(() => {
    if (!selectedSchedule) return 0;
    return selectedSchedule.totalAmount - (selectedSchedule.paidAmount || 0);
  }, [selectedSchedule]);

  const remainingBalance = loan.dueAmount || 0;

  // Auto-fill amount when installment changes
  useEffect(() => {
    if (installmentNumber > 0 && remainingForSelected > 0) {
      setAmount(remainingForSelected);
    }
  }, [installmentNumber, remainingForSelected]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setInstallmentNumber(0);
      setAmount(0);
      setPaymentMethod("");
      setPaymentDate(format(new Date(), "yyyy-MM-dd"));
      setNotes("");
      setReceiptUrl("");
      setShowSuccess(false);
      setErrors({});
    }
  }, [open]);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};

    if (!installmentNumber || installmentNumber <= 0) {
      errs.installment = "Please select an installment";
    }
    if (!amount || amount <= 0) {
      errs.amount = "Please enter a valid amount";
    } else if (amount > remainingForSelected && remainingForSelected > 0) {
      errs.amount = `Amount cannot exceed ${formatCurrency(remainingForSelected)}`;
    }
    if (!paymentMethod) {
      errs.method = "Please select a payment method";
    }
    const date = new Date(paymentDate);
    if (!paymentDate || isNaN(date.getTime())) {
      errs.date = "Please select a valid date";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [installmentNumber, amount, remainingForSelected, paymentMethod, paymentDate]);

  const handleSubmit = useCallback(() => {
    if (!validate() || isLoading) return;

    onSubmit({
      installmentNumber,
      amount,
      paymentMethod,
      paymentDate,
      notes,
      ...(mode === "member" && receiptUrl ? { receiptUrl } : {}),
    });
  }, [validate, isLoading, onSubmit, installmentNumber, amount, paymentMethod, paymentDate, notes, receiptUrl, mode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[960px] p-0 gap-0 rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <DialogHeader className="sr-only">
          <DialogTitle>{mode === "admin" ? "Record Loan Payment" : "Pay Loan"}</DialogTitle>
          <DialogDescription>Process a loan payment</DialogDescription>
        </DialogHeader>

        {/* Mobile Header */}
        <div className="sm:hidden flex items-center gap-2 p-4 border-b border-gray-100">
          <button onClick={() => onOpenChange(false)} className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
            <ArrowLeft className="h-4 w-4 text-gray-500" />
          </button>
          <div>
            <p className="text-sm font-bold text-gray-900">{mode === "admin" ? "Record Payment" : "Pay Loan"}</p>
            <p className="text-[11px] text-gray-400">{loan.loanCode}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">
          {/* ─── LEFT SIDEBAR ───────────────────────────────────────────── */}
          <div className="sm:w-[30%] sm:max-w-[320px] bg-gray-50/50 sm:border-r border-gray-100 p-4 sm:p-5 space-y-4 overflow-y-auto">
            <div className="hidden sm:block">
              <p className="text-sm font-bold text-gray-900 mb-0.5">{mode === "admin" ? "Record Payment" : "Pay Loan"}</p>
              <p className="text-xs text-gray-400">{loan.loanCode}</p>
            </div>
            <LoanSummaryCard loan={loan} progress={progress} />
            <UpcomingPaymentCard nextDue={nextDue} />
            <HelpCard />
          </div>

          {/* ─── RIGHT CONTENT ──────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 relative">
            <AnimatePresence mode="wait">
              {showSuccess ? (
                <SuccessOverlay show={true} />
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-6"
                >
                  {/* Step 1 - Installment */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <InstallmentSelect
                      schedule={loan.schedule || []}
                      value={installmentNumber}
                      onChange={(num) => { setInstallmentNumber(num); setErrors(prev => ({ ...prev, installment: "" })); }}
                      disabled={isLoading}
                    />
                    {errors.installment && (
                      <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {errors.installment}
                      </p>
                    )}
                    {selectedSchedule && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-2 rounded-xl bg-blue-50 border border-blue-100 p-3 flex items-center gap-2"
                      >
                        <Info className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        <p className="text-xs text-blue-700">
                          Due {safeFormatDate(selectedSchedule.dueDate, "MMMM dd, yyyy")} &middot;{" "}
                          <span className="font-semibold">{formatCurrency(remainingForSelected)}</span> remaining
                        </p>
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Step 2 - Amount */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="space-y-2"
                  >
                    <Label className="text-sm font-semibold text-gray-700">Step 2 &middot; Payment Amount</Label>
                    <p className="text-xs text-gray-400">Enter the amount to pay</p>
                    <div className="relative">
                      <div className="absolute left-0 top-0 bottom-0 flex items-center pl-4 z-10">
                        <span className="text-sm font-bold text-green-600 bg-green-50 px-2.5 py-1 rounded-lg border border-green-200">
                          FRW
                        </span>
                      </div>
                      <Input
                        type="number"
                        value={amount || ""}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setAmount(val);
                          setErrors(prev => ({ ...prev, amount: "" }));
                        }}
                        className={cn(
                          "h-14 pl-20 pr-4 text-xl font-bold rounded-xl border-2 transition-all",
                          errors.amount ? "border-red-300 bg-red-50 focus-visible:ring-red-200" :
                          "border-gray-200 bg-white focus-visible:border-green-400 focus-visible:ring-green-100"
                        )}
                        placeholder="0"
                        min={1}
                        max={remainingForSelected || undefined}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.amount ? (
                      <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                        <AlertCircle className="h-3 w-3" /> {errors.amount}
                      </p>
                    ) : (
                      <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                        <span>Max allowed: {formatCurrency(remainingForSelected || remainingBalance)}</span>
                        {amount > 0 && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="font-medium text-gray-500"
                          >
                            Remaining: {formatCurrency(Math.max(0, (remainingForSelected || remainingBalance) - amount))}
                          </motion.span>
                        )}
                      </div>
                    )}
                  </motion.div>

                  {/* Step 3 - Payment Method */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <PaymentMethodSelector
                      value={paymentMethod}
                      onChange={(val) => { setPaymentMethod(val); setErrors(prev => ({ ...prev, method: "" })); }}
                      disabled={isLoading}
                    />
                    {errors.method && (
                      <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {errors.method}
                      </p>
                    )}
                  </motion.div>

                  {/* Step 4 - Payment Date */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-2"
                  >
                    <Label className="text-sm font-semibold text-gray-700">Step 4 &middot; Payment Date</Label>
                    <p className="text-xs text-gray-400">When was this payment made?</p>
                    <div className="relative">
                      <div className="absolute left-0 top-0 bottom-0 flex items-center pl-3 z-10 pointer-events-none">
                        <Calendar className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => {
                          setPaymentDate(e.target.value);
                          setErrors(prev => ({ ...prev, date: "" }));
                        }}
                        className={cn(
                          "h-12 pl-10 pr-4 rounded-xl border-2 transition-all",
                          errors.date ? "border-red-300 bg-red-50" :
                          "border-gray-200 focus-visible:border-green-400 focus-visible:ring-green-100"
                        )}
                        max={format(new Date(), "yyyy-MM-dd")}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.date && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {errors.date}
                      </p>
                    )}
                  </motion.div>

                  {/* Step 5 - Reference */}
                  {mode === "member" && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                      className="space-y-2"
                    >
                      <Label className="text-sm font-semibold text-gray-700">Step 5 &middot; Receipt / Proof</Label>
                      <p className="text-xs text-gray-400">Upload a receipt image or PDF (optional)</p>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setReceiptUrl(file.name);
                        }}
                        className="h-12 rounded-xl border-2 border-dashed border-gray-200 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-green-50 file:text-green-700 file:text-xs file:font-semibold hover:file:bg-green-100"
                        disabled={isLoading}
                      />
                      {receiptUrl && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Selected: {receiptUrl}
                        </p>
                      )}
                    </motion.div>
                  )}

                  {/* Step 6 - Notes */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-2"
                  >
                    <Label className="text-sm font-semibold text-gray-700">Step {mode === "member" ? "6" : "5"} &middot; Notes</Label>
                    <p className="text-xs text-gray-400">Transaction reference or additional information (optional)</p>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={mode === "member" ? "Payment reference, transaction ID, Momo number..." : "Payment reference, transaction ID..."}
                      className="min-h-[80px] rounded-xl border-2 border-gray-200 focus-visible:border-green-400 focus-visible:ring-green-100 resize-none"
                      maxLength={500}
                      disabled={isLoading}
                    />
                    <div className="flex justify-end">
                      <span className="text-[10px] text-gray-400">{notes.length}/500</span>
                    </div>
                  </motion.div>

                  {/* Salary Deduction Notice */}
                  {paymentMethod === "Salary Deduction" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="rounded-xl bg-blue-50 border border-blue-200 p-3"
                    >
                      <div className="flex items-start gap-2">
                        <Building2 className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-blue-700">Payroll Deduction</p>
                          <p className="text-[11px] text-blue-600/80 mt-0.5">
                            {mode === "member"
                              ? "Your payment will be sent to HR for payroll processing after leader approval. The amount will be deducted from your next salary."
                              : "This payment will be processed as a salary deduction."}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Payment Summary */}
                  {installmentNumber > 0 && amount > 0 && paymentMethod && (
                    <PaymentSummaryBar
                      amount={amount}
                      remainingBalance={remainingForSelected || remainingBalance}
                      installmentNumber={installmentNumber}
                      paymentMethod={paymentMethod}
                      paymentDate={paymentDate}
                    />
                  )}

                  {/* Action Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="flex items-center justify-between gap-3 pt-2 sticky bottom-0 bg-white pb-1"
                  >
                    <Button
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      disabled={isLoading}
                      className="h-12 px-6 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className={cn(
                        "h-12 px-8 rounded-xl text-white font-semibold shadow-lg transition-all",
                        "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700",
                        "disabled:opacity-60 disabled:cursor-not-allowed",
                        "flex items-center gap-2"
                      )}
                    >
                      {isLoading ? (
                        <><Loader className="h-4 w-4 animate-spin" /> Processing...</>
                      ) : (
                        <><DollarSign className="h-4 w-4" /> {mode === "admin" ? "Record Payment" : "Submit Payment"}</>
                      )}
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
